import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store for tracking booth visits on floor maps.
 * Persists visited booths to local storage so users don't lose their progress.
 */
const useBoothStore = create(
  persist(
    (set, get) => ({
      // Map of fairId -> Set of visited booth IDs
      visitedBoothsByFair: {},

      // Currently selected fair for the floor map
      selectedFairId: null,

      // Mark a booth as visited
      markBoothVisited: (fairId, boothId) =>
        set(state => {
          const currentVisited = state.visitedBoothsByFair[fairId] || [];
          if (currentVisited.includes(boothId)) {
            return state; // Already visited
          }
          return {
            visitedBoothsByFair: {
              ...state.visitedBoothsByFair,
              [fairId]: [...currentVisited, boothId],
            },
          };
        }),

      // Unmark a booth (remove from visited)
      unmarkBoothVisited: (fairId, boothId) =>
        set(state => {
          const currentVisited = state.visitedBoothsByFair[fairId] || [];
          return {
            visitedBoothsByFair: {
              ...state.visitedBoothsByFair,
              [fairId]: currentVisited.filter(id => id !== boothId),
            },
          };
        }),

      // Toggle booth visited status
      toggleBoothVisited: (fairId, boothId) => {
        const state = get();
        const currentVisited = state.visitedBoothsByFair[fairId] || [];
        if (currentVisited.includes(boothId)) {
          state.unmarkBoothVisited(fairId, boothId);
        } else {
          state.markBoothVisited(fairId, boothId);
        }
      },

      // Check if a booth is visited
      isBoothVisited: (fairId, boothId) => {
        const state = get();
        const currentVisited = state.visitedBoothsByFair[fairId] || [];
        return currentVisited.includes(boothId);
      },

      // Get all visited booths for a fair
      getVisitedBooths: fairId => {
        const state = get();
        return state.visitedBoothsByFair[fairId] || [];
      },

      // Get visited count for a fair
      getVisitedCount: fairId => {
        const state = get();
        return (state.visitedBoothsByFair[fairId] || []).length;
      },

      // Clear all visited booths for a fair
      clearVisitedBooths: fairId =>
        set(state => ({
          visitedBoothsByFair: {
            ...state.visitedBoothsByFair,
            [fairId]: [],
          },
        })),

      // Set the selected fair
      setSelectedFairId: fairId => set({ selectedFairId: fairId }),
    }),
    {
      name: 'booth-visits-storage',
      // Only persist visitedBoothsByFair
      partialize: state => ({ visitedBoothsByFair: state.visitedBoothsByFair }),
    }
  )
);

export default useBoothStore;
